import NavbarV2 from '../../components/navbarV2/NavbarV2';
import ListEmptyStateV2, { ListPageLoadingV2 } from '../../components/listEmptyStateV2/ListEmptyStateV2';
import './OrdersV2.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight, FaClipboardList } from 'react-icons/fa';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { formatMoney, combinedAmount } from '../../utils/formatMoney';

const OrdersV2 = () => {
    const [orders, setOrders] = useState([]);
    const [initialFetchDone, setInitialFetchDone] = useState(false);
    const [page, setPage] = useState(1);
    const [query, setQuery] = useState("");
    const [filter, setFilter] = useState("");
    const navigate = useNavigate();
    const isAdmin = localStorage.getItem('admin') === "true";
    const prevSearch = useRef(null);

    const [debouncedQuery, flushQuery] = useDebouncedValue(query, 350);
    const [debouncedFilter, flushFilter] = useDebouncedValue(filter, 350);

    const loadOrders = useCallback((index, searchQ, searchF) => {
        let url = `${process.env.REACT_APP_API}/api/get${isAdmin ? 'AllOrders' : ('Orders/' + localStorage.getItem('account'))}`;
        const params = new URLSearchParams();
        if (searchQ.length > 0) params.set('query', searchQ);
        if (searchF.length > 0) params.set('filter', searchF);
        params.set('page', String(index));
        url += '?' + params.toString();

        fetch(url, {})
            .then(async (response) => {
                if (response.status === 200) {
                    if (localStorage.getItem('account') !== null) {
                        const data = await response.json();
                        setOrders(data);
                    } else {
                        navigate('/');
                    }
                }
            })
            .catch(() => {})
            .finally(() => setInitialFetchDone(true));
    }, [isAdmin, navigate]);

    useEffect(() => {
        if (!prevSearch.current) {
            prevSearch.current = { q: debouncedQuery, f: debouncedFilter };
            loadOrders(page, debouncedQuery, debouncedFilter);
            return;
        }
        const searchChanged =
            prevSearch.current.q !== debouncedQuery ||
            prevSearch.current.f !== debouncedFilter;

        if (searchChanged) {
            prevSearch.current = { q: debouncedQuery, f: debouncedFilter };
            if (page !== 1) {
                setPage(1);
            } else {
                loadOrders(1, debouncedQuery, debouncedFilter);
            }
            return;
        }

        loadOrders(page, debouncedQuery, debouncedFilter);
    }, [page, debouncedQuery, debouncedFilter, loadOrders]);

    const runSearchNow = () => {
        flushQuery();
        flushFilter();
        setPage(1);
    };

    const clearFilters = () => {
        setQuery('');
        setFilter('');
        flushQuery();
        flushFilter();
        setPage(1);
    };

    const hasActiveFilters = Boolean(debouncedQuery || debouncedFilter);

    const getStatusClass = (status) => {
        switch (status?.toUpperCase()) {
            case 'SUBMITTED': return 'ordersv2-status--submitted';
            case 'APPROVED': return 'ordersv2-status--approved';
            case 'DECLINED': return 'ordersv2-status--declined';
            case 'SHIPPED': return 'ordersv2-status--shipped';
            default: return '';
        }
    };

    return (
        <div className="ordersv2">
            <NavbarV2 />

            <div className="ordersv2-header">
                <h1 className="ordersv2-title">{isAdmin ? 'Orders' : 'Quotations'}</h1>
            </div>

            {isAdmin && (
                <div className="ordersv2-filters">
                    <div className="ordersv2-field">
                        <label>Search</label>
                        <input
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Order #, invoice #, name, email, phone…"
                        />
                    </div>
                    <div className="ordersv2-field">
                        <label>Status</label>
                        <select value={filter} onChange={(e) => setFilter(e.target.value)}>
                            <option value="">ALL</option>
                            <option value="SUBMITTED">SUBMITTED</option>
                            <option value="APPROVED">APPROVED</option>
                            <option value="DECLINED">DECLINED</option>
                            <option value="SHIPPED">SHIPPED</option>
                        </select>
                    </div>
                    <div className="ordersv2-filter-actions">
                        <button type="button" className="ordersv2-btn ordersv2-btn--primary" onClick={runSearchNow}>Search now</button>
                        <button type="button" className="ordersv2-btn ordersv2-btn--secondary" onClick={() => navigate('/createcreditnote')}>Create Credit Note</button>
                    </div>
                </div>
            )}

            {isAdmin && (
                <div className="ordersv2-pagination">
                    <button type="button" className="ordersv2-page-btn" onClick={() => page > 1 && setPage(page - 1)} disabled={page <= 1}>
                        <FaChevronLeft size={10} /> Previous
                    </button>
                    <p className="ordersv2-page-num">{page}</p>
                    <button type="button" className="ordersv2-page-btn" onClick={() => setPage(page + 1)}>
                        Next <FaChevronRight size={10} />
                    </button>
                </div>
            )}

            {!initialFetchDone && <ListPageLoadingV2 />}

            {initialFetchDone && orders.length === 0 && !isAdmin && (
                <ListEmptyStateV2
                    icon={FaClipboardList}
                    title="No quotations yet"
                    description="When you request a quote from your cart, it will show up here so you can track status and totals."
                    primaryAction={{ to: '/', label: 'Browse products' }}
                />
            )}

            {initialFetchDone && orders.length === 0 && isAdmin && hasActiveFilters && (
                <ListEmptyStateV2
                    icon={FaClipboardList}
                    title="No orders found"
                    description="Nothing matched this search or status filter. Try different keywords or reset filters."
                    secondaryAction={{ label: 'Clear filters', onClick: clearFilters }}
                />
            )}

            {initialFetchDone && orders.length === 0 && isAdmin && !hasActiveFilters && (
                <ListEmptyStateV2
                    icon={FaClipboardList}
                    title="No orders yet"
                    description="Customer orders and quotations will appear in this list once they are created."
                />
            )}

            {orders.length > 0 && (
                <div className="ordersv2-table-wrapper">
                    <table className="ordersv2-table">
                        <thead>
                            <tr>
                                <th>Order #</th>
                                <th>Date</th>
                                <th>Shipping</th>
                                <th>Status</th>
                                <th>Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map((row) => (
                                <tr key={row._id} onClick={() => navigate(`/order/${row._id}`)}>
                                    <td className="ordersv2-order-num">{row.orderNumber || row.id}</td>
                                    <td>{new Intl.DateTimeFormat("en-GB", { year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(row.date || row.createdAt))}</td>
                                    <td>
                                        <div className="ordersv2-shipping-info">
                                            <p>{row.deliveryname || row.deliveryName}</p>
                                            <p>{row.deliverystreet || row.deliveryStreet}</p>
                                            <p>{row.deliverycity || row.deliveryCity}</p>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`ordersv2-status ${getStatusClass(row.status)}`}>
                                            {row.status}
                                        </span>
                                    </td>
                                    <td className="ordersv2-total">€{formatMoney(combinedAmount(row))}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default OrdersV2;
